import { FormikHelpers } from 'formik';
import {
  API_FUNCTION_TYPE,
  API_FUNCTION_TYPE_TARGET,
  API_ACCOUNT_FINANCE_RENT,
  API_ACCOUNT_DETAIL_RENT,
  API_ACCOUNT_FINANCE_RENT_RECALCULATE,
  API_ACCOUNT_FINANCE_RENT_RESET,
  API_ACCOUNT_DETAIL_FINANCE_RENT_RESET,
} from 'api/endpoints';
import { FormStore } from 'stores/formStore';
import { computed, action, observable } from 'mobx';
import { ColumnData } from 'components/Graph/ColumnGraph';
import i18n from 'i18next';
import { RootStore } from 'stores/rootStore';
import { RentData, rentGraphColors } from './model';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';
import { CalculationMode } from 'common/interfaces';

export class RentStore extends FormStore<RentData> {
  rentModalsStore: RentModalsStore;
  endpoint: API_FUNCTION_TYPE;
  resetEndpoint: API_FUNCTION_TYPE_TARGET;

  @observable calculating = false;

  constructor(rootStore: RootStore, accountId: string, rentModalsStore: RentModalsStore, detailMode?: boolean) {
    super(rootStore);
    this.accountId = accountId;
    this.rentModalsStore = rentModalsStore || undefined;
    this.endpoint = detailMode ? API_ACCOUNT_DETAIL_RENT : API_ACCOUNT_FINANCE_RENT;
    this.resetEndpoint = detailMode ? API_ACCOUNT_DETAIL_FINANCE_RENT_RESET : API_ACCOUNT_FINANCE_RENT_RESET;
  }

  fetchRent = async (newType?: CalculationMode) => {
    this.rentModalsStore.setLoading(true);
    if (newType) {
      const response = await this.rootStore.stores.apiStore.patch<RentData>(
        this.resetEndpoint(this.accountId, newType),
      );
      if (response?.data) this.data = response.data;
    } else {
      this.calculating = true;
      await this.fetchData(this.endpoint(this.accountId), { resetData: false });
      // get current, actual data
      const currentResponse = await this.rootStore.stores.apiStore.post<RentData>(API_ACCOUNT_FINANCE_RENT_RECALCULATE(this.accountId), this.data);
      if (currentResponse?.data) {
        this.data = currentResponse?.data;
      }
      this.calculating = false;
    }
    this.rentModalsStore.setLoading(false);
  };

  handleSubmit = async (values: RentData, formikHelpers: FormikHelpers<RentData>) => {
    return await this.submitData(this.endpoint(this.accountId), values, {
      formikHelpers,
      reset: false,
      singleValueResponse: true,
      validationPrefixDepth: 3,
    });
  };

  @action
  recalculate = async (values: RentData, formikHelpers: FormikHelpers<RentData>) => {
    await this.submitData(API_ACCOUNT_FINANCE_RENT_RECALCULATE(this.accountId), values, {
      formikHelpers,
      singleValueResponse: false,
      validationPrefixDepth: 3,
    });
  };

  /**
   * Extracts chart data and format them for Graph component
   */
  @computed
  get chart(): ColumnData[] {
    if (!this.data || !this.data.chart) return [];
    const notStacked = ['requiredTotal'];
    const { chart } = this.data;

    return Object.keys(chart)
      .filter((key) => (chart as any)[key].value)
      .map((key) => ({
        name: i18n.t(`finQuestionnary_rent_graph_${key}`),
        stacking: notStacked.includes(key) ? undefined : 'normal',
        color: rentGraphColors[key].default,
        data: [(chart as any)[key].value],
      }));
  }
}
